// Sprint BF — Admin ticket-first detail page.
// Renders the ticket + linked order + event + organiser summary and a safe
// resend-history panel. Provides one-click PDF download and Resend actions
// with confirmation modals. Never exposes provider payloads or auth tokens.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send, FileText, Ticket as TicketIcon, CalendarDays, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { adminApi, adminTicketPdfUrl, centsToAUD, formatAuDate } from "../../lib/events";
import { extractError } from "../../lib/api";

const ACTIVE_STATUSES = new Set(["valid", "checked_in"]);

export default function AdminTicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resendOpen, setResendOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const j = await adminApi.getTicket(ticketId);
      setData(j);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const isActive = data ? ACTIVE_STATUSES.has(data.ticket.status) : false;
  const invalidLabel = data?.ticket?.invalid_reason || null;

  if (loading) {
    return (
      <section className="p-10 flex items-center gap-3" data-testid="admin-ticket-detail-loading">
        <Loader2 className="animate-spin" size={16} />
        <span className="text-sm text-[var(--gt-ink-2)]">Loading ticket…</span>
      </section>
    );
  }
  if (error || !data) {
    return (
      <section className="p-8" data-testid="admin-ticket-detail-error">
        <Link to="/dashboard/admin?tab=orders" className="gt-btn-ghost text-sm"><ArrowLeft size={14}/> Back to Orders &amp; Tickets</Link>
        <div className="mt-6 text-sm text-[var(--gt-accent)]">{error || "Ticket not found."}</div>
      </section>
    );
  }

  const { ticket, order, event, organiser, resend_history } = data;

  return (
    <section className="p-6 sm:p-10 space-y-6" data-testid="admin-ticket-detail-page">
      <nav className="text-xs text-[var(--gt-ink-3)] flex items-center gap-2">
        <Link to="/dashboard/admin?tab=orders" className="hover:text-[var(--gt-accent)]"><ArrowLeft size={12}/> Back to Orders &amp; Tickets</Link>
      </nav>

      <div className="border-2 border-[var(--gt-ink)] p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="uppercase tracking-widest text-[10px] text-[var(--gt-ink-3)]">Ticket</div>
            <h1 className="text-2xl font-black tracking-tight font-mono" data-testid="admin-ticket-code">{ticket.code}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span
                className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${isActive ? "border-[var(--gt-line)]" : "border-[var(--gt-accent)] text-[var(--gt-accent)]"}`}
                data-testid="admin-ticket-status"
              >{ticket.status}</span>
              {ticket.checked_in_at && (
                <span className="text-[10px] text-[var(--gt-ink-2)]" data-testid="admin-ticket-checked-in">
                  Checked in {formatAuDate(ticket.checked_in_at)}
                </span>
              )}
            </div>
            {invalidLabel && (
              <div className="mt-2 text-xs text-[var(--gt-accent)] font-semibold" data-testid="admin-ticket-invalid-warning">
                ⚠︎ {invalidLabel} — historical copy only, not valid admission.
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={adminTicketPdfUrl(ticket.id, { historical: !isActive })}
              target="_blank" rel="noreferrer"
              className="gt-btn-ghost text-xs"
              data-testid="admin-ticket-download-btn"
              title={isActive ? "Download ticket PDF" : "Download historical (watermarked) copy"}
            >
              <FileText size={14}/> {isActive ? "Download PDF" : "Download historical PDF"}
            </a>
            <button
              type="button"
              className="gt-btn-primary text-xs"
              disabled={!isActive}
              onClick={() => setResendOpen(true)}
              data-testid="admin-ticket-resend-btn"
              title={isActive ? "Resend this ticket by email" : "Cannot resend — ticket is not active"}
            >
              <Send size={14}/> Resend ticket
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs pt-3 border-t border-[var(--gt-line)]">
          <div>
            <div className="gt-label mb-0.5">Ticket type</div>
            <div>{ticket.ticket_type_name}</div>
          </div>
          <div>
            <div className="gt-label mb-0.5">Seat</div>
            <div>
              {ticket.seat_label
                ? `${ticket.section || ""}${ticket.row_label ? " Row " + ticket.row_label : ""} ${ticket.seat_label}`.trim()
                : "General admission"}
            </div>
          </div>
          <div>
            <div className="gt-label mb-0.5">Price</div>
            <div className="font-mono">{centsToAUD(ticket.price_cents || 0)}</div>
          </div>
        </div>
      </div>

      {/* Order & Event & Organiser */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-[var(--gt-line)] p-3 space-y-1 text-xs">
          <div className="gt-label flex items-center gap-1"><TicketIcon size={12}/> Order</div>
          <div className="font-mono text-xs" data-testid="admin-ticket-order-id">{order.id}</div>
          <div>{order.buyer_name}</div>
          <div className="text-[var(--gt-ink-2)]">{order.buyer_email}</div>
          {order.buyer_phone && <div className="text-[var(--gt-ink-2)]">{order.buyer_phone}</div>}
          <div className="text-[var(--gt-ink-2)]">{formatAuDate(order.created_at)}</div>
          <div className="font-mono font-bold">{centsToAUD(order.amount_total_cents || 0)}</div>
          <Link to={`/dashboard/admin/orders/${order.id}`} className="gt-btn-ghost !inline-flex text-[10px] mt-2" data-testid="admin-ticket-open-order-btn">
            Open full order
          </Link>
        </div>
        <div className="border border-[var(--gt-line)] p-3 space-y-1 text-xs">
          <div className="gt-label flex items-center gap-1"><CalendarDays size={12}/> Event</div>
          <div className="font-semibold">{event.title}</div>
          <div className="text-[var(--gt-ink-2)]">{formatAuDate(event.start_at)}</div>
          <div className="flex items-center gap-1 text-[var(--gt-ink-2)]"><MapPin size={10}/> {event.venue_name || "—"}{event.state ? `, ${event.state}` : ""}</div>
        </div>
        <div className="border border-[var(--gt-line)] p-3 space-y-1 text-xs">
          <div className="gt-label flex items-center gap-1"><Building2 size={12}/> Organiser</div>
          <div className="font-semibold">{organiser.business_name || organiser.name || "—"}</div>
          <div className="text-[var(--gt-ink-2)]">{organiser.email || "—"}</div>
        </div>
      </div>

      {/* Resend history */}
      <div>
        <div className="gt-label mb-2">Resend / delivery history ({resend_history.length})</div>
        <div className="border border-[var(--gt-line)] bg-white overflow-x-auto" data-testid="admin-ticket-history">
          <table className="w-full text-xs">
            <thead className="bg-[var(--gt-bg-marketing)]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
                <th className="p-2">Date</th>
                <th className="p-2">Kind</th>
                <th className="p-2">Recipient</th>
                <th className="p-2 text-right">Tickets</th>
                <th className="p-2">Status</th>
                <th className="p-2">Recipient mode</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--gt-line)]">
              {resend_history.map((h) => (
                <tr key={h.id}>
                  <td className="p-2">{formatAuDate(h.created_at)}</td>
                  <td className="p-2">{h.kind}</td>
                  <td className="p-2 font-mono text-[10px]">{h.to}</td>
                  <td className="p-2 text-right font-mono">{h.tickets_count || 0}</td>
                  <td className="p-2">{h.status}</td>
                  <td className="p-2">{h.recipient_mode}</td>
                </tr>
              ))}
              {resend_history.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-[var(--gt-ink-3)]">No delivery history yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {resendOpen && (
        <TicketResendDialog
          ticket={ticket}
          order={order}
          event={event}
          organiser={organiser}
          onClose={() => setResendOpen(false)}
          onSent={() => { setResendOpen(false); load(); }}
        />
      )}
    </section>
  );
}

function TicketResendDialog({ ticket, order, event, organiser, onClose, onSent }) {
  const [mode, setMode] = useState("original");
  const [altEmail, setAltEmail] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);

  const canSend = useMemo(() => {
    if (sending) return false;
    if (mode === "alternate") {
      if (!altEmail.trim() || !/^\S+@\S+\.\S+$/.test(altEmail)) return false;
      if (!reason.trim()) return false;
    }
    return true;
  }, [sending, mode, altEmail, reason]);

  const submit = async () => {
    setSending(true);
    try {
      const payload = {
        recipient_mode: mode,
        alternate_email: mode === "alternate" ? altEmail.trim() : null,
        reason: reason.trim() || null,
      };
      const r = await adminApi.resendSingleTicket(ticket.id, payload);
      toast.success(`Sent ${r.tickets_sent} ticket to ${r.recipient} · ${r.attachments_count} PDF attached`);
      onSent();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="admin-ticket-resend-dialog">
        <DialogHeader>
          <DialogTitle>Resend ticket</DialogTitle>
          <DialogDescription>
            Confirm the details below before sending. The QR code will remain unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="border border-[var(--gt-line)] p-3 bg-[var(--gt-bg-marketing)] text-xs space-y-1">
            <div><span className="gt-label">Event</span>: <strong>{event.title}</strong></div>
            <div><span className="gt-label">Organiser</span>: <strong>{organiser.business_name || organiser.name}</strong></div>
            <div><span className="gt-label">Buyer</span>: {order.buyer_name} · {order.buyer_email}</div>
            <div><span className="gt-label">Ticket</span>: <span className="font-mono">{ticket.code}</span> · {ticket.ticket_type_name}</div>
            {ticket.seat_label && <div><span className="gt-label">Seat</span>: {ticket.section || ""} {ticket.row_label ? "Row " + ticket.row_label + " " : ""}{ticket.seat_label}</div>}
          </div>

          <div>
            <div className="gt-label mb-2">Send to</div>
            <label className="flex items-center gap-2 py-1 cursor-pointer">
              <input type="radio" checked={mode === "original"} onChange={() => setMode("original")} data-testid="ticket-resend-mode-original" />
              <span>Original customer email — <strong>{order.buyer_email || "(none)"}</strong></span>
            </label>
            <label className="flex items-center gap-2 py-1 cursor-pointer">
              <input type="radio" checked={mode === "alternate"} onChange={() => setMode("alternate")} data-testid="ticket-resend-mode-alternate" />
              <span>Another email address</span>
            </label>
            {mode === "alternate" && (
              <div className="mt-2 space-y-2 pl-6">
                <input type="email" className="gt-input w-full text-sm" placeholder="customer-assistant@example.com" value={altEmail} onChange={(e) => setAltEmail(e.target.value)} data-testid="ticket-resend-alt-email" />
              </div>
            )}
          </div>

          <div>
            <div className="gt-label mb-1">Reason / internal note{mode === "alternate" ? " (required)" : " (optional)"}</div>
            <textarea className="gt-input w-full text-sm" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="ticket-resend-reason" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" className="gt-btn-ghost text-sm" onClick={onClose} disabled={sending} data-testid="ticket-resend-cancel-btn">Cancel</button>
          <button type="button" className="gt-btn-primary text-sm" disabled={!canSend} onClick={submit} data-testid="ticket-resend-submit-btn">
            {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Send Ticket
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
